/**
 * Data utilities for validation, normalization, filtering, and transformation.
 * Provides pure functions for data manipulation without mutations.
 */

import type {
  ChartTimeRange,
  ComparisonConfig,
  DataGap,
  OhlcvPoint,
  PricePoint,
  ProcessedPricePoint,
  TickerSeries,
  ValidationResult,
} from "~types/types";

// ==================== Validation ====================

/**
 * Validate a single price point.
 */
export function validatePricePoint(point: unknown): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (typeof point !== "object" || point === null) {
    errors.push("Point must be an object");

    return { valid: false, errors, warnings };
  }

  const p = point as Record<string, unknown>;

  if (
    typeof p.timestamp !== "number" ||
    !Number.isFinite(p.timestamp) ||
    p.timestamp <= 0
  ) {
    errors.push("timestamp must be a positive finite number");
  }

  if (typeof p.value !== "number" || !Number.isFinite(p.value)) {
    errors.push("value must be a finite number");
  }

  if (
    p.volume !== undefined &&
    (typeof p.volume !== "number" || !Number.isFinite(p.volume) || p.volume < 0)
  ) {
    errors.push("volume must be a non-negative finite number");
  }

  return { valid: errors.length === 0, errors, warnings };
}

/**
 * Validate an OHLCV point.
 */
function validateOhlcvConstraints(
  high: number,
  low: number,
  open: number,
  close: number,
  errors: string[]
): void {
  if (high < low) errors.push("high must be >= low");
  if (high < open) errors.push("high must be >= open");
  if (high < close) errors.push("high must be >= close");
  if (low > open) errors.push("low must be <= open");
  if (low > close) errors.push("low must be <= close");
}

export function validateOhlcvPoint(point: unknown): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (typeof point !== "object" || point === null) {
    errors.push("Point must be an object");

    return { valid: false, errors, warnings };
  }

  const p = point as Record<string, unknown>;

  if (
    typeof p.timestamp !== "number" ||
    !Number.isFinite(p.timestamp) ||
    p.timestamp <= 0
  ) {
    errors.push("timestamp must be a positive finite number");
  }

  const requiredFields = ["open", "high", "low", "close"];

  for (const field of requiredFields) {
    if (typeof p[field] !== "number" || !Number.isFinite(p[field])) {
      errors.push(`${field} must be a finite number`);
    }
  }

  if (errors.length === 0) {
    const { open, high, low, close } = p as {
      open: number;
      high: number;
      low: number;
      close: number;
    };

    validateOhlcvConstraints(high, low, open, close, errors);
  }

  if (
    p.volume !== undefined &&
    (typeof p.volume !== "number" || !Number.isFinite(p.volume) || p.volume < 0)
  ) {
    errors.push("volume must be a non-negative finite number");
  }

  return { valid: errors.length === 0, errors, warnings };
}

/**
 * Validate a complete series.
 */
export function validateSeries(series: unknown): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (typeof series !== "object" || series === null) {
    errors.push("Series must be an object");

    return { valid: false, errors, warnings };
  }

  const s = series as Record<string, unknown>;

  if (!Array.isArray(s.priceMovements)) {
    errors.push("priceMovements must be an array");

    return { valid: false, errors, warnings };
  }

  if (s.priceMovements.length === 0) {
    errors.push("priceMovements must be non-empty");
  }

  const movements = s.priceMovements as unknown[];
  let prevTimestamp = -Infinity;

  for (let i = 0; i < movements.length; i++) {
    const point = movements[i];
    const isOhlcv =
      typeof point === "object" && point !== null && "open" in point;
    const result = isOhlcv
      ? validateOhlcvPoint(point)
      : validatePricePoint(point);

    if (!result.valid) {
      errors.push(`Point at index ${i}: ${result.errors.join(", ")}`);
    }

    if (typeof point === "object" && point !== null && "timestamp" in point) {
      const ts = (point as { timestamp: number }).timestamp;

      if (ts <= prevTimestamp) {
        warnings.push(
          `Points not sorted: index ${i} timestamp ${ts} <= previous ${prevTimestamp}`
        );
      }
      prevTimestamp = ts;
    }
  }

  return { valid: errors.length === 0, errors, warnings };
}

// ==================== Sorting ====================

/**
 * Sort data points by timestamp (ascending).
 * Returns new array, does not mutate input.
 */
export function sortByTimestamp<T extends { timestamp: number }>(
  data: T[]
): T[] {
  return [...data].sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Check if data is sorted by timestamp.
 */
export function isSortedByTimestamp<T extends { timestamp: number }>(
  data: T[]
): boolean {
  for (let i = 1; i < data.length; i++) {
    const current = data[i];
    const prev = data[i - 1];

    if (current && prev && current.timestamp < prev.timestamp) {
      return false;
    }
  }

  return true;
}

// ==================== Normalization ====================

/**
 * Get the base value for normalization.
 * @param data - Price points array
 * @param config - Comparison configuration
 * @param visibleRange - Optional visible time range
 * @param previousClose - Optional previous close value from series
 */
export function getNormalizationBase(
  data: PricePoint[],
  config: ComparisonConfig,
  visibleRange?: ChartTimeRange,
  previousClose?: number
): number | null {
  if (data.length === 0) return null;

  const base = config.normalizationBase ?? "first-data";

  if (typeof base === "number") {
    return base;
  }

  if (base === "previous-close") {
    // Use previousClose if available, otherwise fall back to first data point
    if (previousClose !== undefined && previousClose !== 0) {
      return previousClose;
    }

    return data[0]?.value ?? null;
  }

  if (base === "first-data") {
    return data[0]?.value ?? null;
  }

  if (base === "first-visible" && visibleRange) {
    const visiblePoint = data.find((p) => p.timestamp >= visibleRange.start);

    return visiblePoint?.value ?? data[0]?.value ?? null;
  }

  return data[0]?.value ?? null;
}

/**
 * Normalize series data for comparison mode.
 * Converts prices to index values (100 = base value).
 */
export function normalizeSeries(
  series: TickerSeries,
  config: ComparisonConfig,
  visibleRange?: ChartTimeRange
): PricePoint[] {
  const data = series.priceMovements as PricePoint[];
  const baseValue = getNormalizationBase(
    data,
    config,
    visibleRange,
    series.previousClose
  );

  if (baseValue === null || baseValue === 0) {
    return data.map((p) => ({ ...p, value: 100 }));
  }

  return data.map((p) => ({
    ...p,
    value: (p.value / baseValue) * 100,
  }));
}

/**
 * Normalize series data to percentage change for comparison mode.
 * Computes percentage change from baseline: ((value - baseline) / baseline) * 100
 *
 * Baseline determination (in order of priority):
 * 1. previousClose from series (if available and normalizationBase is 'previous-close' or not specified)
 * 2. First data point's price
 * 3. Fallback to 1 to avoid division by zero
 */
export function normalizeToPercentageChange(
  series: TickerSeries,
  config: ComparisonConfig,
  visibleRange?: ChartTimeRange
): PricePoint[] {
  const data = series.priceMovements as PricePoint[];

  if (data.length === 0) {
    return [];
  }

  // Determine baseline using previousClose as default for percentage mode
  const configWithPreviousCloseDefault: ComparisonConfig = {
    ...config,
    normalizationBase: config.normalizationBase ?? "previous-close",
  };

  const rawBaseValue = getNormalizationBase(
    data,
    configWithPreviousCloseDefault,
    visibleRange,
    series.previousClose
  );

  // Use 1 as fallback to avoid division by zero
  const baseValue =
    rawBaseValue === null || rawBaseValue === 0 ? 1 : rawBaseValue;

  return data.map((p) => ({
    ...p,
    value: ((p.value - baseValue) / baseValue) * 100,
  }));
}

// ==================== Filtering ====================

/**
 * Filter data to visible time range.
 * Includes one point before and after for line continuity.
 */
export function filterToRange<T extends { timestamp: number }>(
  data: T[],
  range: ChartTimeRange
): T[] {
  if (data.length === 0) return [];

  let startIndex = 0;
  let endIndex = data.length - 1;

  // Binary search for start
  let left = 0;
  let right = data.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midPoint = data[mid];

    if (!midPoint) break;
    if (midPoint.timestamp < range.start) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }
  startIndex = Math.max(0, left - 1);

  // Binary search for end
  left = 0;
  right = data.length - 1;
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midPoint = data[mid];

    if (!midPoint) break;
    if (midPoint.timestamp <= range.end) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }
  endIndex = Math.min(data.length - 1, right + 1);

  return data.slice(startIndex, endIndex + 1);
}

/**
 * Extract numeric value from a data point.
 * Returns `value` for PricePoint, `close` for OhlcvPoint.
 */
export function getPointValue(
  point: PricePoint | OhlcvPoint | { timestamp: number; value?: number }
): number {
  if ("value" in point && typeof point.value === "number") return point.value;
  if (
    "close" in point &&
    typeof (point as unknown as { close: number }).close === "number"
  ) {
    return (point as unknown as { close: number }).close;
  }

  return 0;
}

function computeBucketAvg<T extends { timestamp: number; value?: number }>(
  data: T[],
  start: number,
  end: number
): { x: number; y: number } {
  const avg = { x: 0, y: 0 };
  let count = 0;

  for (let j = start; j < end; j++) {
    const point = data[j];

    if (!point) continue;
    avg.x += point.timestamp;
    avg.y += getPointValue(point);
    count++;
  }

  if (count > 0) {
    avg.x /= count;
    avg.y /= count;
  }

  return avg;
}

function findMaxAreaIndex<T extends { timestamp: number; value?: number }>(
  data: T[],
  bucketStart: number,
  bucketEnd: number,
  prevPoint: T,
  nextAvg: { x: number; y: number }
): number {
  let maxArea = -1;
  let maxIndex = bucketStart;
  const prevValue = getPointValue(prevPoint);

  for (let j = bucketStart; j < bucketEnd && j < data.length; j++) {
    const point = data[j];

    if (!point) continue;

    const area = Math.abs(
      (prevPoint.timestamp - nextAvg.x) * (getPointValue(point) - prevValue) -
        (prevPoint.timestamp - point.timestamp) * (nextAvg.y - prevValue)
    );

    if (area > maxArea) {
      maxArea = area;
      maxIndex = j;
    }
  }

  return maxIndex;
}

export function downsample<T extends { timestamp: number; value?: number }>(
  data: T[],
  targetPoints: number
): T[] {
  if (data.length <= targetPoints) return data;

  const firstPoint = data[0];

  if (!firstPoint) return data;

  const result: T[] = [firstPoint];
  const bucketSize = (data.length - 2) / (targetPoints - 2);

  for (let i = 0; i < targetPoints - 2; i++) {
    const bucketStart = Math.floor((i + 1) * bucketSize) + 1;
    const bucketEnd = Math.floor((i + 2) * bucketSize) + 1;
    const nextStart = Math.floor((i + 2) * bucketSize) + 1;
    const nextEnd = Math.min(Math.floor((i + 3) * bucketSize) + 1, data.length);

    const prevPoint = result.at(-1);

    if (!prevPoint) continue;

    const nextAvg = computeBucketAvg(data, nextStart, nextEnd);
    const maxIndex = findMaxAreaIndex(
      data,
      bucketStart,
      bucketEnd,
      prevPoint,
      nextAvg
    );
    const selectedPoint = data[maxIndex];

    if (selectedPoint) result.push(selectedPoint);
  }

  const lastPoint = data.at(-1);

  if (lastPoint) result.push(lastPoint);

  return result;
}

// ==================== Merging ====================

/**
 * Merge new points into existing data.
 * Handles duplicates (same timestamp) by using new value.
 * Maintains sort order.
 */
export function mergePoints<T extends { timestamp: number }>(
  existing: T[],
  newPoints: T[]
): T[] {
  if (newPoints.length === 0) return existing;
  if (existing.length === 0) return sortByTimestamp(newPoints);

  if (!isSortedByTimestamp(existing) || !isSortedByTimestamp(newPoints)) {
    const merged = new Map<number, T>();

    for (const point of existing) merged.set(point.timestamp, point);
    for (const point of newPoints) merged.set(point.timestamp, point);

    return Array.from(merged.values()).sort(
      (a, b) => a.timestamp - b.timestamp
    );
  }

  const result: T[] = [];
  let i = 0;
  let j = 0;

  while (i < existing.length && j < newPoints.length) {
    const e = existing[i]!;
    const n = newPoints[j]!;

    if (e.timestamp < n.timestamp) {
      result.push(e);
      i++;
    } else if (e.timestamp > n.timestamp) {
      result.push(n);
      j++;
    } else {
      result.push(n);
      i++;
      j++;
    }
  }

  while (i < existing.length) {
    result.push(existing[i]!);
    i++;
  }

  while (j < newPoints.length) {
    result.push(newPoints[j]!);
    j++;
  }

  return result;
}

/**
 * Append points to the end of data.
 * More efficient than merge when we know points are newer.
 */
export function appendPoints<T extends { timestamp: number }>(
  existing: T[],
  newPoints: T[]
): T[] {
  if (newPoints.length === 0) return existing;
  if (existing.length === 0) return sortByTimestamp(newPoints);

  const lastPoint = existing.at(-1);

  if (!lastPoint) return [...existing, ...sortByTimestamp(newPoints)];

  const lastTimestamp = lastPoint.timestamp;
  const filtered = newPoints.filter((p) => p.timestamp > lastTimestamp);

  return [...existing, ...sortByTimestamp(filtered)];
}

// ==================== Trimming ====================

/**
 * Trim data to maximum number of points.
 * Removes oldest points first.
 */
export function trimToMaxPoints<T>(data: T[], maxPoints: number): T[] {
  if (data.length <= maxPoints) return data;

  return data.slice(data.length - maxPoints);
}

/**
 * Trim data to time range.
 * Removes points outside the range.
 */
export function trimToTimeRange<T extends { timestamp: number }>(
  data: T[],
  range: ChartTimeRange
): T[] {
  return data.filter(
    (p) => p.timestamp >= range.start && p.timestamp <= range.end
  );
}

/**
 * Align comparison series to the primary ticker's time range.
 * Trims points outside the primary's start/end, and fills edges
 * with the nearest known value so comparison lines span the full chart.
 */
export function alignSeriesToPrimaryRange<
  T extends { timestamp: number; value: number; volume?: number }
>(data: T[], primaryStart: number, primaryEnd: number): T[] {
  if (data.length === 0) return [];

  // Trim to primary's range
  const trimmed = data.filter(
    (p) => p.timestamp >= primaryStart && p.timestamp <= primaryEnd
  );

  if (trimmed.length === 0) {
    // No overlap — find closest point and fill entire range with its value
    const closest =
      data.find((p) => p.timestamp >= primaryStart) ?? data.at(-1);

    if (!closest) return [];

    return [
      { ...closest, timestamp: primaryStart, volume: undefined } as T,
      { ...closest, timestamp: primaryEnd, volume: undefined } as T,
    ];
  }

  const result = [...trimmed];
  const firstTrimmed = trimmed[0];
  const lastTrimmed = trimmed.at(-1);

  // Backward-fill: if trimmed data starts after primary start,
  // add a point at primaryStart with the first known value
  if (firstTrimmed && firstTrimmed.timestamp > primaryStart) {
    result.unshift({
      ...firstTrimmed,
      timestamp: primaryStart,
      volume: undefined,
    } as T);
  }

  // Forward-fill: if trimmed data ends before primary end,
  // add a point at primaryEnd with the last known value
  if (lastTrimmed && lastTrimmed.timestamp < primaryEnd) {
    result.push({
      ...lastTrimmed,
      timestamp: primaryEnd,
      volume: undefined,
    } as T);
  }

  return result;
}

// ==================== Domain Extraction ====================

/**
 * Extract time domain (min/max timestamp) from data.
 */
export function getTimeDomain<T extends { timestamp: number }>(
  data: T[]
): [number, number] | null {
  if (data.length === 0) return null;

  let min = Infinity;
  let max = -Infinity;

  for (const point of data) {
    if (point.timestamp < min) min = point.timestamp;
    if (point.timestamp > max) max = point.timestamp;
  }

  return [min, max];
}

/**
 * Extract value domain (min/max value) from price points.
 */
export function getValueDomain(data: PricePoint[]): [number, number] | null {
  if (data.length === 0) return null;

  let min = Infinity;
  let max = -Infinity;

  for (const point of data) {
    if (point.value < min) min = point.value;
    if (point.value > max) max = point.value;
  }

  return [min, max];
}

/**
 * Extract value domain from OHLCV points (uses high/low).
 */
export function getOhlcvDomain(data: OhlcvPoint[]): [number, number] | null {
  if (data.length === 0) return null;

  let min = Infinity;
  let max = -Infinity;

  for (const point of data) {
    if (point.low < min) min = point.low;
    if (point.high > max) max = point.high;
  }

  return [min, max];
}

/**
 * Extract volume domain from data.
 */
export function getVolumeDomain<T extends { volume?: number }>(
  data: T[]
): [number, number] | null {
  if (data.length === 0) return null;

  let min = Infinity;
  let max = -Infinity;

  for (const point of data) {
    if (point.volume !== undefined) {
      if (point.volume < min) min = point.volume;
      if (point.volume > max) max = point.volume;
    }
  }

  if (min === Infinity || max === -Infinity) return null;

  return [Math.min(min, 0), max];
}

/**
 * Get combined domain across multiple series.
 */
export function getCombinedDomain(
  seriesData: Map<string, PricePoint[]>
): { time: [number, number]; value: [number, number] } | null {
  if (seriesData.size === 0) return null;

  let timeMin = Infinity;
  let timeMax = -Infinity;
  let valueMin = Infinity;
  let valueMax = -Infinity;

  for (const data of seriesData.values()) {
    const timeDomain = getTimeDomain(data);
    const valueDomain = getValueDomain(data);

    if (timeDomain) {
      timeMin = Math.min(timeMin, timeDomain[0]);
      timeMax = Math.max(timeMax, timeDomain[1]);
    }

    if (valueDomain) {
      valueMin = Math.min(valueMin, valueDomain[0]);
      valueMax = Math.max(valueMax, valueDomain[1]);
    }
  }

  if (timeMin === Infinity || valueMin === Infinity) return null;

  return {
    time: [timeMin, timeMax],
    value: [valueMin, valueMax],
  };
}

// ==================== Transformation ====================

/**
 * Convert OHLCV to simple price points (using close).
 */
export function ohlcvToPricePoints(data: OhlcvPoint[]): PricePoint[] {
  return data.map((p) => ({
    timestamp: p.timestamp,
    value: p.close,
    volume: p.volume,
  }));
}

/**
 * Extract close prices from OHLCV data.
 */
export function extractClosePrices(data: OhlcvPoint[]): number[] {
  return data.map((p) => p.close);
}

/**
 * Get the typical interval between data points.
 */
export function getMedianInterval<T extends { timestamp: number }>(
  data: T[]
): number | null {
  if (data.length < 2) return null;

  const intervals: number[] = [];

  for (let i = 1; i < data.length; i++) {
    const current = data[i];
    const prev = data[i - 1];

    if (!current || !prev) continue;
    intervals.push(current.timestamp - prev.timestamp);
  }

  if (intervals.length === 0) return null;

  intervals.sort((a, b) => a - b);
  const mid = Math.floor(intervals.length / 2);

  if (intervals.length % 2 === 0) {
    const val1 = intervals[mid - 1];
    const val2 = intervals[mid];

    if (val1 === undefined || val2 === undefined) return null;

    return (val1 + val2) / 2;
  }

  return intervals[mid] ?? null;
}

// ==================== Gap Detection & Filling ====================

/**
 * Detect gaps in data using explicit interval threshold.
 * Returns array of gaps with metadata about size and whether they are "large".
 */
export function detectGaps(data: PricePoint[], intervalMs: number): DataGap[] {
  if (data.length < 2) return [];

  const threshold = intervalMs * 3;
  const gaps: DataGap[] = [];

  for (let i = 1; i < data.length; i++) {
    const current = data[i];
    const prev = data[i - 1];

    if (!current || !prev) continue;

    const gapSize = current.timestamp - prev.timestamp;

    if (gapSize > intervalMs) {
      gaps.push({
        start: prev.timestamp,
        end: current.timestamp,
        size: gapSize,
        isLarge: gapSize > threshold,
      });
    }
  }

  return gaps;
}

/**
 * Forward-fill all gaps using LOCF (Last Observation Carried Forward).
 * Returns new array with filled points marked with isFilled: true.
 */
function fillGapPoints(
  result: ProcessedPricePoint[],
  current: PricePoint,
  gap: DataGap,
  intervalMs: number
): void {
  let fillTimestamp = current.timestamp + intervalMs;

  while (fillTimestamp < gap.end) {
    result.push({
      timestamp: fillTimestamp,
      value: current.value,
      volume: undefined,
      isFilled: true,
    });
    fillTimestamp += intervalMs;
  }
}

export function fillGaps(
  data: PricePoint[],
  gaps: DataGap[],
  intervalMs: number
): ProcessedPricePoint[] {
  if (data.length === 0) return [];
  if (gaps.length === 0) return data.map((p) => ({ ...p }));

  const result: ProcessedPricePoint[] = [];
  let gapIndex = 0;

  for (const current of data) {
    if (!current) continue;

    result.push({ ...current });

    // Process all gaps that start at the current point
    while (gapIndex < gaps.length) {
      const gap = gaps[gapIndex];

      if (!gap) break;

      if (gap.start === current.timestamp) {
        fillGapPoints(result, current, gap, intervalMs);
        gapIndex++;
      } else if (gap.start > current.timestamp) {
        break;
      } else {
        gapIndex++;
      }
    }
  }

  return result;
}

/**
 * Resample all series to a common time grid (first series' timestamps).
 * Uses forward-fill for missing points in secondary series.
 * Used in comparison mode to align series with different time points.
 */
function resampleSeriesData(
  data: ProcessedPricePoint[],
  commonTimestamps: number[]
): ProcessedPricePoint[] {
  const result: ProcessedPricePoint[] = [];
  let dataIndex = 0;
  let lastValue = data[0]?.value ?? 0;

  for (const timestamp of commonTimestamps) {
    // Advance index to the point at or just before this timestamp
    while (dataIndex < data.length - 1) {
      const nextPoint = data[dataIndex + 1];

      if (!nextPoint || nextPoint.timestamp > timestamp) break;

      const point = data[dataIndex];

      if (point) lastValue = point.value;
      dataIndex++;
    }

    const currentPoint = data[dataIndex];

    if (currentPoint?.timestamp === timestamp) {
      result.push({ ...currentPoint });
      lastValue = currentPoint.value;
    } else {
      // Forward-fill with last known value
      result.push({
        timestamp,
        value: lastValue,
        volume: undefined,
        isFilled: true,
      });
    }
  }

  return result;
}

export function resampleToCommonGrid(
  seriesData: Map<string, ProcessedPricePoint[]>
): Map<string, ProcessedPricePoint[]> {
  const seriesIds = Array.from(seriesData.keys());

  if (seriesIds.length === 0) return seriesData;

  const firstSeriesId = seriesIds[0];

  if (!firstSeriesId) return seriesData;

  const firstSeriesData = seriesData.get(firstSeriesId);

  if (!firstSeriesData || firstSeriesData.length === 0) return seriesData;

  const commonTimestamps = firstSeriesData.map((p) => p.timestamp);
  const resampled = new Map<string, ProcessedPricePoint[]>();

  // First series stays as-is
  resampled.set(firstSeriesId, firstSeriesData);

  for (let i = 1; i < seriesIds.length; i++) {
    const seriesId = seriesIds[i];

    if (!seriesId) continue;

    const data = seriesData.get(seriesId);

    if (!data || data.length === 0) {
      resampled.set(seriesId, []);
      continue;
    }

    resampled.set(seriesId, resampleSeriesData(data, commonTimestamps));
  }

  return resampled;
}

/**
 * Apply LTTB downsampling to data.
 * Target: ~1 point per 2 pixels of chart width.
 */
export function applyAutoDownsampling(
  data: ProcessedPricePoint[],
  chartWidthPx: number
): ProcessedPricePoint[] {
  const targetPoints = Math.max(10, Math.floor(chartWidthPx / 2));

  if (data.length <= targetPoints) return data;

  return downsample(data, targetPoints);
}
